import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

export const SUPPORTED_LANGS = ["en", "ru"] as const;
export type Lang = (typeof SUPPORTED_LANGS)[number];
export const LANG_STORAGE_KEY = "dv-lang";

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { en: {}, ru: {} },
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGS,
    nonExplicitSupportedLngs: true,
    defaultNS: "common",
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: LANG_STORAGE_KEY,
      caches: ["localStorage"],
    },
    returnNull: false,
  });

/** Return the active 2-letter language code. Avoid `i18n.resolvedLanguage`
 * directly — its getter returns `undefined` in this i18next version even when
 * `i18n.language` is set to a supported language. Read from `i18n.language`
 * and normalize (`"en-US"` → `"en"`) against `SUPPORTED_LANGS`. */
/** The detector's localStorage cache (`dv-lang`) holds the language i18next
 * will converge to. Read it directly: i18next's `changeLanguage` to the
 * detected language resolves asynchronously, so `i18n.language` is still the
 * bare fallback on the first synchronous render — reading it there would fetch
 * EN content for a RU user and flash on switch. */
function persistedLang(): string | undefined {
  try {
    return localStorage.getItem(LANG_STORAGE_KEY) || undefined;
  } catch {
    return undefined;
  }
}

export function getActiveLang(): Lang {
  const source =
    persistedLang() ||
    i18n.language ||
    (typeof navigator !== "undefined" ? navigator.language : "") ||
    "en";
  const raw = source.split("-")[0].toLowerCase();
  return (SUPPORTED_LANGS as readonly string[]).includes(raw) ? (raw as Lang) : "en";
}

// Reflect the active language on <html lang> for screen readers + SEO.
if (typeof document !== "undefined") {
  const applyHtmlLang = (lng?: string) => {
    document.documentElement.lang = lng ? lng.split("-")[0] : getActiveLang();
  };
  applyHtmlLang();
  i18n.on("languageChanged", applyHtmlLang);
}

export default i18n;
